<template>
  <div class="totp-verify">
    <!-- Low backup codes warning after TOTP verification -->
    <div v-if="showLowCodesWarning && remainingCodes !== null" class="verification-state">
      <div class="text-center">
        <div class="icon-container">
          <div class="icon-wrapper">
            <LucideIcon name="ShieldAlert" :size="32" class="text-muted" />
          </div>
        </div>
        <h2 class="title">Verification Successful</h2>
        <p class="subtitle">Signing you in...</p>
        <div class="warning-box">
          <div class="warning-content">
            <LucideIcon name="AlertTriangle" :size="16" class="warning-icon" />
            <span class="warning-text">
              {{
                remainingCodes === 0
                  ? 'No backup codes remaining! Reset 2FA in settings to generate new codes.'
                  : `Only ${remainingCodes} backup code${remainingCodes === 1 ? '' : 's'} remaining`
              }}
            </span>
          </div>
        </div>
      </div>
      <div class="loading-container">
        <LucideIcon name="Loader2" :size="24" class="loading-spinner" />
      </div>
    </div>

    <!-- Success state - redirecting (no backup code warning) -->
    <div v-else-if="isRedirecting" class="verification-state">
      <div class="text-center">
        <div class="icon-container">
          <div class="icon-wrapper">
            <LucideIcon name="ShieldAlert" :size="32" class="text-muted" />
          </div>
        </div>
        <h2 class="title">Verification Successful</h2>
        <p class="subtitle">Signing you in...</p>
      </div>
      <div class="loading-container">
        <LucideIcon name="Loader2" :size="24" class="loading-spinner" />
      </div>
    </div>

    <!-- Success message after backup code verification -->
    <div v-else-if="remainingCodes !== null && mode === 'backup'" class="verification-state">
      <div class="text-center">
        <div class="icon-container">
          <div class="icon-wrapper">
            <LucideIcon name="KeyRound" :size="32" class="text-muted" />
          </div>
        </div>
        <h2 class="title">Backup Code Accepted</h2>
        <p class="subtitle">Signing you in...</p>
        <div v-if="remainingCodes <= 3" class="warning-box">
          <div class="warning-content">
            <LucideIcon name="AlertTriangle" :size="16" class="warning-icon" />
            <span class="warning-text">
              {{
                remainingCodes === 0
                  ? 'No backup codes remaining!'
                  : `Only ${remainingCodes} backup code${remainingCodes === 1 ? '' : 's'} remaining`
              }}
            </span>
          </div>
        </div>
      </div>
      <div class="loading-container">
        <LucideIcon name="Loader2" :size="24" class="loading-spinner" />
      </div>
    </div>

    <!-- Backup code entry mode -->
    <div v-else-if="mode === 'backup'" class="entry-mode">
      <!-- Header -->
      <div class="text-center">
        <div class="icon-container">
          <div class="icon-wrapper">
            <LucideIcon name="KeyRound" :size="32" class="text-muted" />
          </div>
        </div>
        <h2 class="title">Use Backup Code</h2>
        <p class="subtitle">Enter one of your single-use backup codes</p>
      </div>

      <!-- Backup Code Input -->
      <div class="input-section">
        <label for="backup-code" class="input-label">Backup Code</label>
        <input
          id="backup-code"
          ref="backupInput"
          type="text"
          placeholder="XXXX-XXXX"
          :value="backupCode"
          @input="handleBackupCodeChange"
          @keypress.enter="handleBackupCodeKeyPress"
          :disabled="isVerifying"
          class="backup-input"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="characters"
          spellcheck="false"
        />
        <p class="input-hint">Format: XXXX-XXXX (alphanumeric)</p>
        <div v-if="error" class="error-message">{{ error }}</div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button
          type="button"
          class="btn btn-primary"
          @click="handleVerifyBackupCode"
          :disabled="!isValidBackupCodeFormat(backupCode) || isVerifying"
        >
          <LucideIcon v-if="isVerifying" name="Loader2" :size="16" class="btn-icon loading-spinner" />
          {{ isVerifying ? 'Verifying...' : 'Use Backup Code' }}
        </button>

        <!-- Switch to TOTP Link -->
        <div class="text-center">
          <button
            type="button"
            class="link-button"
            @click="switchToTOTPMode"
            :disabled="isVerifying"
          >
            Use authenticator app instead
          </button>
        </div>

        <!-- Back Button -->
        <button
          type="button"
          class="btn btn-ghost"
          @click="handleBack"
          :disabled="isVerifying"
        >
          <LucideIcon name="ArrowLeft" :size="16" class="btn-icon" />
          Back to login
        </button>
      </div>
    </div>

    <!-- TOTP code entry mode (default) -->
    <div v-else class="entry-mode">
      <!-- Header -->
      <div class="text-center">
        <div class="icon-container">
          <div class="icon-wrapper">
            <LucideIcon name="ShieldAlert" :size="32" class="text-muted" />
          </div>
        </div>
        <h2 class="title">Two-Factor Authentication</h2>
        <p class="subtitle">Enter the 6-digit code from your authenticator app</p>
      </div>

      <!-- 6-Digit Input Grid -->
      <div class="digit-section">
        <div class="digit-grid">
          <input
            v-for="(digit, index) in digits"
            :key="index"
            :ref="`digitInput${index}`"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="1"
            :value="digit"
            @input="handleDigitChange(index, $event)"
            @keydown="handleKeyDown(index, $event)"
            @paste="handlePaste(index, $event)"
            :disabled="isVerifying"
            :class="[
              'digit-input',
              { 'digit-error': error, 'digit-disabled': isVerifying }
            ]"
            :autocomplete="index === 0 ? 'one-time-code' : 'off'"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            :name="`otp-${index}`"
            :aria-label="`Digit ${index + 1} of 6`"
          />
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message">{{ error }}</div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button
          type="button"
          class="btn btn-primary"
          @click="handleVerifyClick"
          :disabled="!allDigitsEntered || isVerifying"
        >
          <LucideIcon v-if="isVerifying" name="Loader2" :size="16" class="btn-icon loading-spinner" />
          {{ isVerifying ? 'Verifying...' : 'Verify Code' }}
        </button>

        <!-- Backup Code Link -->
        <div class="text-center">
          <button
            type="button"
            class="link-button"
            @click="switchToBackupMode"
            :disabled="isVerifying"
          >
            Use a backup code instead
          </button>
        </div>

        <!-- Back Button -->
        <button
          type="button"
          class="btn btn-ghost"
          @click="handleBack"
          :disabled="isVerifying"
        >
          <LucideIcon name="ArrowLeft" :size="16" class="btn-icon" />
          Back to login
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import LucideIcon from '../LucideIcon/LucideIcon.vue';

type VerifyMode = 'totp' | 'backup';

/**
 * TOTPVerify - Clean 6-digit code input for authentication
 *
 * Features:
 * - Auto-advance between digits
 * - Paste support for full 6-digit codes
 * - Clear error messaging
 * - Keyboard navigation (backspace, arrow keys)
 * - Backup code alternative
 *
 * IMPORTANT: This component uses MOCK verification only.
 * Production implementation MUST call backend API endpoints for:
 * - TOTP code verification
 * - Backup code verification
 * - Rate limiting
 * - Audit logging
 *
 * See: C:\azagent\CassieCloud\Vue.Frontends\context\TOTP_SPA_VS_SSR_SECURITY_ANALYSIS.md
 */
export default Vue.extend({
  name: 'TOTPVerify',

  components: {
    LucideIcon
  },

  props: {
    email: {
      type: String,
      required: true
    },
    onComplete: {
      type: Function,
      required: true
    },
    onBack: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      mode: 'totp' as VerifyMode,
      digits: ['', '', '', '', '', ''] as string[],
      backupCode: '',
      error: '',
      isVerifying: false,
      isRedirecting: false,
      remainingCodes: null as number | null,
      showLowCodesWarning: false
    };
  },

  computed: {
    allDigitsEntered(): boolean {
      return this.digits.every((d: string) => d !== '');
    }
  },

  mounted() {
    // Auto-focus first input on mount
    if (this.mode === 'totp') {
      this.focusDigitInput(0);
    } else {
      this.focusBackupInput();
    }
  },

  watch: {
    mode(newMode: VerifyMode) {
      // Focus appropriate input when mode changes
      this.$nextTick(() => {
        if (newMode === 'totp') {
          this.focusDigitInput(0);
        } else {
          this.focusBackupInput();
        }
      });
    }
  },

  methods: {
    focusDigitInput(index: number) {
      const input = this.$refs[`digitInput${index}`] as HTMLInputElement[];
      if (input && input[0]) {
        input[0].focus();
      }
    },

    focusBackupInput() {
      const input = this.$refs.backupInput as HTMLInputElement;
      if (input) {
        input.focus();
      }
    },

    handleDigitChange(index: number, event: Event) {
      const target = event.target as HTMLInputElement;
      const value = target.value;

      // Only allow numeric input
      const numericValue = value.replace(/\D/g, '');

      if (numericValue.length === 0) {
        // Clear the current digit
        const newDigits = [...this.digits];
        newDigits[index] = '';
        this.digits = newDigits;
        this.error = '';
        return;
      }

      // Handle single digit input
      if (numericValue.length === 1) {
        const newDigits = [...this.digits];
        newDigits[index] = numericValue;
        this.digits = newDigits;
        this.error = '';

        // Auto-advance to next input
        if (index < 5) {
          this.focusDigitInput(index + 1);
        } else {
          // All digits entered, verify automatically
          this.verifyDigits([...newDigits]);
        }
        return;
      }

      // Handle paste of multiple digits
      if (numericValue.length > 1) {
        this.handlePasteValue(numericValue);
      }
    },

    handleKeyDown(index: number, event: KeyboardEvent) {
      if (event.key === 'Backspace') {
        if (this.digits[index] === '' && index > 0) {
          // If current digit is empty, move to previous and clear it
          const newDigits = [...this.digits];
          newDigits[index - 1] = '';
          this.digits = newDigits;
          this.focusDigitInput(index - 1);
          this.error = '';
        } else if (this.digits[index] !== '') {
          // Clear current digit
          const newDigits = [...this.digits];
          newDigits[index] = '';
          this.digits = newDigits;
          this.error = '';
        }
      } else if (event.key === 'ArrowLeft' && index > 0) {
        this.focusDigitInput(index - 1);
      } else if (event.key === 'ArrowRight' && index < 5) {
        this.focusDigitInput(index + 1);
      }
    },

    handlePaste(index: number, event: ClipboardEvent) {
      event.preventDefault();
      const pastedData = event.clipboardData?.getData('text') || '';
      this.handlePasteValue(pastedData);
    },

    handlePasteValue(pastedValue: string) {
      // Extract only numeric characters
      const numericValue = pastedValue.replace(/\D/g, '').slice(0, 6);

      if (numericValue.length === 6) {
        const newDigits = numericValue.split('');
        this.digits = newDigits;
        this.error = '';
        // Focus last input
        this.focusDigitInput(5);
        // Verify automatically after paste
        this.verifyDigits(newDigits);
      } else if (numericValue.length > 0) {
        // Partial paste
        const newDigits = [...this.digits];
        for (let i = 0; i < numericValue.length && i < 6; i++) {
          newDigits[i] = numericValue[i];
        }
        this.digits = newDigits;
        this.error = '';
        // Focus next empty input
        const nextIndex = Math.min(numericValue.length, 5);
        this.focusDigitInput(nextIndex);
      }
    },

    async verifyDigits(digitsToVerify: string[]) {
      const code = digitsToVerify.join('');
      if (code.length !== 6) {
        return;
      }

      this.isVerifying = true;
      this.error = '';

      // Simulate slight delay for better UX
      await new Promise((resolve) => setTimeout(resolve, 500));

      // MOCK VERIFICATION: Production MUST call backend API
      // Example: await this.$api.totp.verify(code);
      const isValid = this.mockVerifyCode(this.email, code);

      if (isValid) {
        // Check if backup codes are running low (MOCK data)
        const remaining = this.mockGetRemainingBackupCodeCount(this.email);
        if (remaining <= 3 && remaining >= 0) {
          this.remainingCodes = remaining;
          this.showLowCodesWarning = true;
          // Brief pause to show warning, then complete
          await new Promise((resolve) => setTimeout(resolve, 2000));
        } else {
          // Show redirecting state for successful verification
          this.isRedirecting = true;
          // Brief pause to show success before redirect
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
        // Keep isVerifying true - don't re-enable button during redirect
        this.onComplete();
      } else {
        this.error = 'Invalid code. Check your authenticator and try again.';
        // Keep inputs populated so users can see what they entered
        // Focus first input to allow correction
        this.focusDigitInput(0);
        const input = this.$refs.digitInput0 as HTMLInputElement[];
        if (input && input[0]) {
          input[0].select();
        }
        this.isVerifying = false;
      }
    },

    handleVerifyClick() {
      this.verifyDigits(this.digits);
    },

    handleBackupCodeChange(event: Event) {
      const target = event.target as HTMLInputElement;
      // Allow alphanumeric and hyphens, uppercase automatically
      const value = target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');

      // Auto-format: insert hyphen after 4 characters if not present
      let formatted = value;
      if (value.length === 4 && !value.includes('-')) {
        formatted = value + '-';
      } else if (value.length > 4 && value[4] !== '-') {
        formatted = value.slice(0, 4) + '-' + value.slice(4);
      }

      this.backupCode = formatted.slice(0, 9); // XXXX-XXXX = 9 chars max
      this.error = '';
    },

    handleBackupCodeKeyPress(event: KeyboardEvent) {
      if (event.key === 'Enter' && this.isValidBackupCodeFormat(this.backupCode)) {
        this.handleVerifyBackupCode();
      }
    },

    isValidBackupCodeFormat(code: string): boolean {
      // Accept with or without hyphen: XXXXXXXX or XXXX-XXXX
      const normalised = code.replace(/-/g, '');
      return normalised.length === 8 && /^[A-Z0-9]+$/.test(normalised);
    },

    async handleVerifyBackupCode() {
      if (!this.isValidBackupCodeFormat(this.backupCode)) {
        this.error = 'Please enter a valid backup code (XXXX-XXXX)';
        return;
      }

      this.isVerifying = true;
      this.error = '';

      // Simulate slight delay for better UX
      await new Promise((resolve) => setTimeout(resolve, 500));

      // MOCK VERIFICATION: Production MUST call backend API
      // Example: await this.$api.totp.verifyBackupCode(this.backupCode);
      const isValid = this.mockVerifyBackupCode(this.email, this.backupCode);

      if (isValid) {
        // Show remaining codes count before completing
        const remaining = this.mockGetRemainingBackupCodeCount(this.email);
        this.remainingCodes = remaining;

        // Brief pause to show the remaining count message
        await new Promise((resolve) => setTimeout(resolve, 1500));
        // Keep isVerifying true - don't re-enable button during redirect
        this.onComplete();
      } else {
        this.error = 'Invalid or already used backup code. Please try another.';
        this.backupCode = '';
        this.focusBackupInput();
        this.isVerifying = false;
      }
    },

    switchToBackupMode() {
      this.mode = 'backup';
      this.error = '';
      this.digits = ['', '', '', '', '', ''];
    },

    switchToTOTPMode() {
      this.mode = 'totp';
      this.error = '';
      this.backupCode = '';
    },

    handleBack() {
      this.onBack();
    },

    // MOCK FUNCTIONS - Replace with actual API calls in production
    mockVerifyCode(_email: string, code: string): boolean {
      // Simple mock: accept "123456" as valid for demo purposes
      // Production: This MUST call backend API endpoint
      return code === '123456';
    },

    mockVerifyBackupCode(_email: string, code: string): boolean {
      // Simple mock: accept "ABCD-1234" as valid for demo purposes
      // Production: This MUST call backend API endpoint
      const normalised = code.replace(/-/g, '').toUpperCase();
      return normalised === 'ABCD1234';
    },

    mockGetRemainingBackupCodeCount(_email: string): number {
      // Mock: return 2 for demo purposes to show warning
      // Production: This MUST call backend API endpoint
      return 2;
    }
  }
});
</script>

<style scoped>
.totp-verify {
  max-width: 500px;
  margin: 0 auto;
}

/* Layout modes */
.verification-state,
.entry-mode {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Text alignment */
.text-center {
  text-align: center;
}

/* Icon styling */
.icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background-color: #f3f4f6;
  border-radius: 50%;
}

.text-muted {
  color: #6b7280;
}

/* Typography */
.title {
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Warning box */
.warning-box {
  margin-top: 16px;
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.03);
  border: 1px solid #e5e7eb;
  border-radius: var(--radius);
}

.warning-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.warning-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.warning-text {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}

/* Loading spinner */
.loading-container {
  display: flex;
  justify-content: center;
}

.loading-spinner {
  animation: spin 1s linear infinite;
  color: #9ca3af;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Digit input section */
.digit-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.digit-grid {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.digit-input {
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 24px;
  font-family: 'Courier New', Courier, monospace;
  font-weight: 600;
  border-radius: var(--radius);
  border: 2px solid #d1d5db;
  background-color: #ffffff;
  color: #111827;
  transition: all 0.15s ease;
}

.digit-input:focus {
  outline: none;
  border-color: var(--primary, #4a7c9b);
  box-shadow: 0 0 0 3px rgba(74, 124, 155, 0.15);
}

.digit-input.digit-error {
  border-color: #ef4444;
  background-color: #fef2f2;
}

.digit-input.digit-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Input section for backup code */
.input-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-label {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.backup-input {
  width: 100%;
  padding: 12px;
  text-align: center;
  font-size: 20px;
  font-family: 'Courier New', Courier, monospace;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 2px solid #d1d5db;
  border-radius: var(--radius);
  background-color: #ffffff;
  color: #111827;
  transition: all 0.15s ease;
}

.backup-input:focus {
  outline: none;
  border-color: var(--primary, #4a7c9b);
  box-shadow: 0 0 0 3px rgba(74, 124, 155, 0.15);
}

.backup-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.input-hint {
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  margin: 0;
}

/* Error message */
.error-message {
  font-size: 14px;
  font-weight: 500;
  color: #dc2626;
  background-color: #fef2f2;
  padding: 12px;
  border-radius: var(--radius);
  text-align: center;
}

/* Action buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: var(--primary, #4a7c9b);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-dark, #3d6680);
}

.btn-ghost {
  background-color: transparent;
  color: #6b7280;
  border: 1px solid transparent;
}

.btn-ghost:hover:not(:disabled) {
  background-color: #f3f4f6;
  color: #111827;
}

.btn-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.link-button {
  background: none;
  border: none;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  transition: color 0.15s ease;
}

.link-button:hover:not(:disabled) {
  color: #374151;
}

.link-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Dark mode support - only via explicit theme class */
.theme--dark .icon-wrapper {
  background-color: var(--color-background-muted-dark, #374151);
}

.theme--dark .title {
  color: var(--color-text-primary-dark, #f9fafb);
}

.theme--dark .subtitle {
  color: var(--color-text-secondary-dark, #9ca3af);
}

.theme--dark .digit-input {
  border-color: var(--color-border-dark, #4b5563);
  background-color: var(--color-background-dark, #1f2937);
  color: var(--color-text-primary-dark, #f9fafb);
}

.theme--dark .digit-input.digit-error {
  background-color: var(--color-error-bg-dark, rgba(239, 68, 68, 0.1));
}

.theme--dark .backup-input {
  border-color: var(--color-border-dark, #4b5563);
  background-color: var(--color-background-dark, #1f2937);
  color: var(--color-text-primary-dark, #f9fafb);
}

.theme--dark .error-message {
  background-color: var(--color-error-bg-dark, rgba(239, 68, 68, 0.2));
}

.theme--dark .btn-ghost:hover:not(:disabled) {
  background-color: var(--color-background-muted-dark, #374151);
}
</style>
